import { useMutation } from 'react-query'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ScriptAngle } from 'types'

const POLL_INTERVAL_MS = 2000
const TIMEOUT_MS = 30_000

export interface GenerateAvatarScriptVariables {
  brandKitId: number
  avatarId: number
  angle: ScriptAngle
  videoTopic: string
  productReferenceUrl?: string | null
}

interface ScriptStatusResponse {
  status: 'processing' | 'ready' | 'failed'
  script: string | null
  error: string | null
}

export const useGenerateAvatarScript = () => {
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<string, Error, GenerateAvatarScriptVariables>(
    async ({ brandKitId, avatarId, angle, videoTopic, productReferenceUrl }) => {
      const basePath = `/w/${workspaceId}/brand_kits/${brandKitId}/avatars/${avatarId}`
      const startRes = await sendToApi<{ token: string }>(`${basePath}/generate_script`, {
        method: 'POST',
        body: {
          angle,
          video_topic: videoTopic,
          product_reference_url: productReferenceUrl ?? undefined,
        },
      })
      if (startRes.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Failed to start script generation')

      const deadline = Date.now() + TIMEOUT_MS
      do {
        const res = await sendToApi<ScriptStatusResponse>(`${basePath}/script_status?token=${startRes.data.token}`)
        if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Failed to check script status')
        if (res.data.status === 'failed') throw new Error(res.data.error || 'Script generation failed')
        if (res.data.status === 'ready') return res.data.script ?? ''
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
      } while (Date.now() < deadline)

      throw new Error('Script generation timed out')
    },
  )
}
